import React from "react"
import { useQueryClient } from "@tanstack/react-query"
import { usePolling, getPollingQueryOpts } from "../layout/HoaAdminLayout"
import { 
  useAnnouncements,
  useCreateAnnouncement,
  useUpdateAnnouncement,
  useDeleteAnnouncement,
  useAnnouncement
} from "@/api/announcements"
import type { 
  AnnouncementPriority,
  Announcement,
  CreateAnnouncementData,
  UpdateAnnouncementData
} from "@/types/announcement"
import { toast } from "sonner"
import { useLanguage } from "@/contexts/LanguageContext"

/** ---------- Types ---------- */
type Priority = AnnouncementPriority

/** ---------- Tokens / shared styles ---------- */
const PRIMARY = "#1F3A93"
/** Quiet outline: white bg + black text, visible on glass */
const QUIET =
  "!bg-white !text-gray-900 !border-black/10 border ring-1 ring-black/5 shadow-sm rounded-xl hover:!bg-gray-50 active:!bg-gray-50"

function getAnnouncementErrorMessage(
  error: any,
  t: (key: string, params?: Record<string, string | number>) => string,
  fallbackKey: string
): string {
  const message = String(error?.message ?? "")

  if (error?.code === "ECONNABORTED" || /timeout/i.test(message)) {
    return t("error.timeout")
  }
  if (!error?.response) {
    return t("error.network")
  }

  const status = Number(error?.response?.status ?? 0)
  if (status === 401) return t("error.unauthorized")
  if (status === 403) return t("error.forbidden")
  if (status === 404) return t("error.notFound")
  if (status >= 500) return t("error.serverError")

  return t(fallbackKey)
}

/** ---------- Page ---------- */
export default function AnnouncementsPage() {
  const { enabled, intervalMs } = usePolling()
  const { t, language } = useLanguage()

  const dateLocale = language === "es" ? "es-ES" : "en-US"
  const formatDate = React.useCallback(
    (value: string) => new Intl.DateTimeFormat(dateLocale).format(new Date(value)),
    [dateLocale]
  )

  const { data, isLoading } = useAnnouncements()

  const createMutation = useCreateAnnouncement()
  const updateMutation = useUpdateAnnouncement()
  const deleteMutation = useDeleteAnnouncement()

  // Treat HOA_ADMIN as "admin" role for now
  const role = sessionStorage.getItem("role")
  const isAdmin = role === "HOA_ADMIN"

  const [showForm, setShowForm] = React.useState(false)
  const [editingId, setEditingId] = React.useState<string | null>(null)
  const [confirmDelete, setConfirmDelete] = React.useState<Announcement | null>(null)
  const formRef = React.useRef<HTMLDivElement>(null)

  const [form, setForm] = React.useState<{
    title: string
    content: string
    priority: Priority
    published_at: string
    expires_at: string
    is_pinned: boolean
  }>({
    title: "",
    content: "",
    priority: "medium",
    published_at: new Date().toISOString().slice(0, 16),
    expires_at: "",
    is_pinned: false,
  })

  function handleSubmit(e: React.FormEvent) {
    e.preventDefault()
    if (!form.title.trim() || !form.content.trim()) return
    
    // Get user's building/condo from session
    const building = sessionStorage.getItem("condo_id") || ""
    
    const payload: CreateAnnouncementData = {
      title: form.title.trim(),
      content: form.content.trim(),
      priority: form.priority,
      building,
      published_at: form.published_at || undefined,
      expires_at: form.expires_at || undefined,
      is_pinned: form.is_pinned,
    }
    
    createMutation.mutate(payload, {
      onSuccess: () => {
        toast.success(t("hoa.announcements.toast.created"))
        setForm({
          title: "",
          content: "",
          priority: "medium",
          published_at: new Date().toISOString().slice(0, 16),
          expires_at: "",
          is_pinned: false,
        })
        setShowForm(false)
      },
      onError: (error: any) => {
        toast.error(getAnnouncementErrorMessage(error, t, "hoa.announcements.toast.createFailed"))
      },
    })
  }

  const items = (data ?? []).slice()

  function openNewForm() {
    setShowForm(true)
    requestAnimationFrame(() => {
      formRef.current?.scrollIntoView({ behavior: "smooth", block: "start" })
    })
  }

  return (
    <div className="relative space-y-6 sm:space-y-7">
      {/* soft ambient bg to match the app */}
      <div className="pointer-events-none absolute inset-0 -z-10 bg-[radial-gradient(1200px_600px_at_50%_-10%,rgba(31,58,147,0.06),transparent_60%)]" />

      {/* Admin Controls (glass) */}
      {isAdmin && (
        <div className="flex items-center justify-between rounded-2xl bg-white/80 backdrop-blur ring-1 ring-black/5 p-3 sm:p-4">
          <p className="text-sm text-gray-600">
            {isLoading
              ? t("common.loading")
              : t("hoa.announcements.count", { count: items.length })}
          </p>
          <button
            onClick={() => (showForm ? setShowForm(false) : openNewForm())}
            className="inline-flex items-center gap-2 rounded-xl px-4 py-2 text-sm font-semibold text-white shadow-lg shadow-black/10 hover:shadow-xl active:translate-y-[1px]"
            style={{ background: "linear-gradient(90deg,#1F3A93 0%,#1F3A93 70%,#27AE60 100%)" }}
            title={t("hoa.announcements.createTitle")}
          >
            <PlusIcon className="w-5 h-5" />
            {t("hoa.announcements.new")}
          </button>
        </div>
      )}

      {/* New Announcement Form (glass) */}
      {isAdmin && showForm && (
        <div
          ref={formRef}
          className="rounded-3xl bg-white/90 backdrop-blur ring-1 ring-black/5 p-5 sm:p-6 shadow-lg shadow-black/10"
        >
          <h3 className="mb-4 text-[18px] font-semibold text-gray-900">
            {t("hoa.announcements.createNew")}
          </h3>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <label className="mb-1.5 block text-sm text-gray-700">
                {t("common.title")}
              </label>
              <input
                type="text"
                value={form.title}
                onChange={(e) => setForm((f) => ({ ...f, title: e.target.value }))}
                className="w-full rounded-2xl border border-black/10 bg-white px-4 py-3 text-sm outline-none ring-0 focus:ring-2 focus:ring-black/10"
                placeholder={t("hoa.announcements.titlePlaceholder")}
                required
                minLength={3}
              />
            </div>
            <div>
              <label className="mb-1.5 block text-sm text-gray-700">
                {t("common.content")}
              </label>
              <textarea
                value={form.content}
                onChange={(e) => setForm((f) => ({ ...f, content: e.target.value }))}
                className="min-h-24 w-full rounded-2xl border border-black/10 bg-white px-4 py-3 text-sm outline-none ring-0 focus:ring-2 focus:ring-black/10"
                placeholder={t("hoa.announcements.contentPlaceholder")}
                required
                minLength={10}
              />
            </div>
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
              <div>
                <label className="mb-1.5 block text-sm text-gray-700">
                  {t("common.priority")}
                </label>
                <select
                  value={form.priority}
                  onChange={(e) =>
                    setForm((f) => ({ ...f, priority: e.target.value as Priority }))
                  }
                  className="w-full rounded-2xl border border-black/10 bg-white px-4 py-3 text-sm outline-none focus:ring-2 focus:ring-black/10"
                >
                  <option value="low">{t("common.priority.low")}</option>
                  <option value="medium">{t("common.priority.medium")}</option>
                  <option value="high">{t("common.priority.high")}</option>
                  <option value="urgent">{t("common.priority.urgent")}</option>
                </select>
              </div>
              <div>
                <label className="mb-1.5 block text-sm text-gray-700">
                  <div className="flex items-center gap-2">
                    <input
                      type="checkbox"
                      checked={form.is_pinned}
                      onChange={(e) => setForm((f) => ({ ...f, is_pinned: e.target.checked }))}
                      className="h-4 w-4 rounded border-gray-300"
                    />
                    <span>{t("hoa.announcements.pinToTop")}</span>
                  </div>
                </label>
              </div>
            </div>
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
              <div>
                <label className="mb-1.5 block text-sm text-gray-700">
                  {t("hoa.announcements.publishDate")}
                </label>
                <input
                  type="datetime-local"
                  value={form.published_at}
                  onChange={(e) => setForm((f) => ({ ...f, published_at: e.target.value }))}
                  className="w-full rounded-2xl border border-black/10 bg-white px-4 py-3 text-sm outline-none focus:ring-2 focus:ring-black/10"
                />
              </div>
              <div>
                <label className="mb-1.5 block text-sm text-gray-700">
                  {t("hoa.announcements.expiresOptional")}
                </label>
                <input
                  type="datetime-local"
                  value={form.expires_at}
                  onChange={(e) => setForm((f) => ({ ...f, expires_at: e.target.value }))}
                  className="w-full rounded-2xl border border-black/10 bg-white px-4 py-3 text-sm outline-none focus:ring-2 focus:ring-black/10"
                />
              </div>
            </div>
            <div className="flex gap-3">
              <button
                type="submit"
                disabled={createMutation.isPending}
                className="rounded-xl px-4 py-2 text-sm font-semibold text-white shadow-lg shadow-black/10 hover:shadow-xl active:translate-y-[1px] disabled:opacity-50"
                style={{ background: "linear-gradient(90deg,#1F3A93 0%,#1F3A93 70%,#27AE60 100%)" }}
              >
                {createMutation.isPending
                  ? t("hoa.announcements.publishing")
                  : t("hoa.announcements.publish")}
              </button>
              <button
                type="button"
                onClick={() => setShowForm(false)}
                className={`px-4 py-2 text-sm ${QUIET}`}
              >
                {t("common.cancel")}
              </button>
            </div>
          </form>
        </div>
      )}

      {/* Announcements List (glass cards + priority rail) */}
      <div className="space-y-4">
        {(data ?? []).map((a) => {
          const IconComponent = getPriorityIcon(a.priority)
          return (
            <div
              key={a.id}
              className="relative rounded-3xl bg-white/90 backdrop-blur ring-1 ring-black/5 p-5 sm:p-6 shadow-lg shadow-black/10"
            >
              {/* priority left rail */}
              <span
                className="absolute left-0 top-0 h-full w-1.5 rounded-l-3xl"
                style={{ background: getPriorityRail(a.priority) }}
              />
              <div className="mb-3 flex items-start justify-between">
                <div className="flex items-start gap-3">
                  {/* leading icon on white chip */}
                  <div
                    className="rounded-2xl p-2.5 bg-white ring-1 ring-black/10 shadow-sm"
                    style={{ color: getPriorityColor(a.priority) }}
                  >
                    <IconComponent className="h-6 w-6" />
                  </div>
                  <div className="min-w-0 flex-1">
                    <div className="mb-1 flex items-center gap-2">
                      <h3 className="truncate text-[16px] font-semibold text-gray-900">
                        {a.title}
                      </h3>
                      {a.is_pinned && (
                        <span title={t("hoa.announcements.pinned")}>
                          <PinIcon className="h-4 w-4 text-amber-600" />
                        </span>
                      )}
                    </div>
                    <div className="flex flex-wrap items-center gap-2 sm:gap-3">
                      <span
                        className="rounded-full px-2.5 py-1 text-xs font-medium ring-1 ring-black/10"
                        style={{
                          background: getPriorityBg(a.priority),
                          color: getPriorityColor(a.priority),
                        }}
                      >
                        {t(`common.priority.${a.priority}`)}
                      </span>
                      <div className="ml-0 inline-flex items-center gap-1 text-xs text-gray-600">
                        <CalendarIcon className="h-4 w-4" />
                        {formatDate(a.published_at)}
                      </div>
                      <span className="text-xs text-gray-600">
                        • {t("hoa.announcements.postedBy", { name: a.author_name || t("common.unknown") })}
                      </span>
                      {a.expires_at && (
                        <span className="text-xs text-gray-500">
                          • {t("hoa.announcements.expiresOn", {
                            date: formatDate(a.expires_at),
                          })}
                        </span>
                      )}
                    </div>
                  </div>
                </div>

                {isAdmin && (
                  <div className="flex items-center gap-2">
                    <button
                      className={`p-2 ${QUIET}`}
                      title={t("common.edit")}
                      onClick={() => setEditingId(a.id)}
                    >
                      <PencilIcon className="h-5 w-5 text-gray-800" />
                    </button>
                    <button
                      className={`p-2 ${QUIET} hover:!bg-red-50`}
                      title={t("common.delete")}
                      onClick={() => setConfirmDelete(a as any)}
                    >
                      <TrashIcon className="h-5 w-5 text-gray-800" />
                    </button>
                  </div>
                )}
              </div>

              {/* Content not available in list view - shown in detail/edit modal */}
            </div>
          )
        })}
      </div>

      {/* Empty state */}
      {!isLoading && (data ?? []).length === 0 && (
        <div className="py-12 text-center">
          <SpeakerIcon className="mx-auto mb-4 h-12 w-12 text-gray-500" />
          <p className="text-sm text-gray-600">{t("hoa.announcements.empty")}</p>
        </div>
      )}

      {/* Edit modal (glass) */}
      {editingId && (
        <EditModalWrapper
          announcementId={editingId}
          onClose={() => setEditingId(null)}
          onSave={(data: UpdateAnnouncementData) => {
            updateMutation.mutate(
              { id: editingId, data },
              {
                onSuccess: () => {
                  toast.success(t("hoa.announcements.toast.updated"))
                  setEditingId(null)
                },
                onError: (error: any) => {
                  toast.error(getAnnouncementErrorMessage(error, t, "hoa.announcements.toast.updateFailed"))
                },
              }
            )
          }}
          saving={updateMutation.isPending}
        />
      )}

      {/* Delete confirmation modal (glass) */}
      {confirmDelete && (
        <DeleteConfirmModal
          title={t("hoa.announcements.deleteTitle")}
          message={t("hoa.announcements.deleteMessage", { title: confirmDelete.title })}
          confirming={deleteMutation.isPending}
          onCancel={() => setConfirmDelete(null)}
          onConfirm={() => {
            deleteMutation.mutate(confirmDelete.id, {
              onSuccess: () => {
                toast.success(t("hoa.announcements.toast.deleted"))
                setConfirmDelete(null)
              },
              onError: (error: any) => {
                toast.error(getAnnouncementErrorMessage(error, t, "hoa.announcements.toast.deleteFailed"))
              },
            })
          }}
        />
      )}
    </div>
  )
}

/** ---------- Edit Modal Wrapper (fetches full announcement) ---------- */
function EditModalWrapper({
  announcementId,
  onClose,
  onSave,
  saving,
}: {
  announcementId: string
  onClose: () => void
  onSave: (data: UpdateAnnouncementData) => void
  saving: boolean
}) {
  const { t } = useLanguage()
  const { data: announcement, isLoading } = useAnnouncement(announcementId)

  if (isLoading) {
    return (
      <div className="fixed inset-0 z-40">
        <div className="absolute inset-0 bg-black/40 backdrop-blur-sm" onClick={onClose} />
        <div className="absolute inset-x-0 bottom-0 sm:inset-auto sm:left-1/2 sm:top-1/2 sm:-translate-x-1/2 sm:-translate-y-1/2 sm:w-full sm:max-w-xl">
          <div className="m-0 rounded-t-3xl sm:rounded-3xl bg-white/90 backdrop-blur ring-1 ring-black/10 p-6 shadow-2xl">
            <p className="text-center text-gray-600">{t("common.loading")}</p>
          </div>
        </div>
      </div>
    )
  }

  if (!announcement) {
    onClose()
    return null
  }

  return <EditModal item={announcement} onClose={onClose} onSave={onSave} saving={saving} />
}

/** ---------- Edit Modal ---------- */
function EditModal({
  item,
  onClose,
  onSave,
  saving,
}: {
  item: Announcement
  onClose: () => void
  onSave: (data: UpdateAnnouncementData) => void
  saving: boolean
}) {
  const { t } = useLanguage()
  const [title, setTitle] = React.useState(item.title)
  const [content, setContent] = React.useState(item.content)
  const [priority, setPriority] = React.useState<Priority>(item.priority)
  const [publishedAt, setPublishedAt] = React.useState(
    new Date(item.published_at).toISOString().slice(0, 16)
  )
  const [expiresAt, setExpiresAt] = React.useState(
    item.expires_at ? new Date(item.expires_at).toISOString().slice(0, 16) : ""
  )
  const [isPinned, setIsPinned] = React.useState(item.is_pinned)

  function submit(e: React.FormEvent) {
    e.preventDefault()
    if (!title.trim() || !content.trim()) return
    onSave({
      title: title.trim(),
      content: content.trim(),
      priority,
      published_at: publishedAt || undefined,
      expires_at: expiresAt || undefined,
      is_pinned: isPinned,
    })
  }

  return (
    <div className="fixed inset-0 z-40">
      <div className="absolute inset-0 bg-black/40 backdrop-blur-sm" onClick={onClose} />
      <div className="absolute inset-x-0 bottom-0 sm:inset-auto sm:left-1/2 sm:top-1/2 sm:-translate-x-1/2 sm:-translate-y-1/2 sm:w-full sm:max-w-xl">
        <form
          onSubmit={submit}
          className="m-0 rounded-t-3xl sm:rounded-3xl bg-white/90 backdrop-blur ring-1 ring-black/10 p-6 shadow-2xl"
        >
          <div className="mb-2 flex items-center justify-between">
            <h3 className="text-[16px] font-semibold text-gray-900">
              {t("hoa.announcements.editTitle")}
            </h3>
            <button type="button" className="text-gray-500 hover:text-gray-700" onClick={onClose}>
              {t("common.close")}
            </button>
          </div>

          <div className="mt-3 space-y-4">
            <div>
              <label className="mb-1.5 block text-sm text-gray-700">
                {t("common.title")}
              </label>
              <input
                value={title}
                onChange={(e) => setTitle(e.target.value)}
                className="w-full rounded-2xl border border-black/10 bg-white px-4 py-3 text-sm outline-none focus:ring-2 focus:ring-black/10"
                required
                minLength={3}
              />
            </div>
            <div>
              <label className="mb-1.5 block text-sm text-gray-700">
                {t("common.content")}
              </label>
              <textarea
                value={content}
                onChange={(e) => setContent(e.target.value)}
                className="min-h-24 w-full rounded-2xl border border-black/10 bg-white px-4 py-3 text-sm outline-none focus:ring-2 focus:ring-black/10"
                required
                minLength={10}
              />
            </div>
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
              <div>
                <label className="mb-1.5 block text-sm text-gray-700">
                  {t("common.priority")}
                </label>
                <select
                  value={priority}
                  onChange={(e) => setPriority(e.target.value as Priority)}
                  className="w-full rounded-2xl border border-black/10 bg-white px-4 py-3 text-sm outline-none focus:ring-2 focus:ring-black/10"
                >
                  <option value="low">{t("common.priority.low")}</option>
                  <option value="medium">{t("common.priority.medium")}</option>
                  <option value="high">{t("common.priority.high")}</option>
                  <option value="urgent">{t("common.priority.urgent")}</option>
                </select>
              </div>
              <div>
                <label className="mb-1.5 block text-sm text-gray-700">
                  <div className="flex items-center gap-2">
                    <input
                      type="checkbox"
                      checked={isPinned}
                      onChange={(e) => setIsPinned(e.target.checked)}
                      className="h-4 w-4 rounded border-gray-300"
                    />
                    <span>{t("hoa.announcements.pinToTop")}</span>
                  </div>
                </label>
              </div>
            </div>
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
              <div>
                <label className="mb-1.5 block text-sm text-gray-700">
                  {t("hoa.announcements.publishDate")}
                </label>
                <input
                  type="datetime-local"
                  value={publishedAt}
                  onChange={(e) => setPublishedAt(e.target.value)}
                  className="w-full rounded-2xl border border-black/10 bg-white px-4 py-3 text-sm outline-none focus:ring-2 focus:ring-black/10"
                />
              </div>
              <div>
                <label className="mb-1.5 block text-sm text-gray-700">
                  {t("hoa.announcements.expiresOptional")}
                </label>
                <input
                  type="datetime-local"
                  value={expiresAt}
                  onChange={(e) => setExpiresAt(e.target.value)}
                  className="w-full rounded-2xl border border-black/10 bg-white px-4 py-3 text-sm outline-none focus:ring-2 focus:ring-black/10"
                />
              </div>
            </div>
          </div>

          <div className="mt-6 flex items-center justify-end gap-2">
            <button type="button" className={`px-4 py-2 text-sm ${QUIET}`} onClick={onClose}>
              {t("common.cancel")}
            </button>
            <button
              type="submit"
              disabled={saving}
              className="rounded-xl px-4 py-2 text-sm font-semibold text-white shadow-lg shadow-black/10 hover:shadow-xl active:translate-y-[1px]"
              style={{ background: "linear-gradient(90deg,#1F3A93 0%,#1F3A93 70%,#27AE60 100%)" }}
            >
              {saving ? t("common.saving") : t("common.saveChanges")}
            </button>
          </div>
        </form>
      </div>
    </div>
  )
}

/** ---------- Delete Confirmation Modal ---------- */
function DeleteConfirmModal({
  title,
  message,
  onCancel,
  onConfirm,
  confirming,
}: {
  title: string
  message: string
  onCancel: () => void
  onConfirm: () => void
  confirming: boolean
}) {
  const { t } = useLanguage()
  return (
    <div className="fixed inset-0 z-40">
      <div className="absolute inset-0 bg-black/40 backdrop-blur-sm" onClick={onCancel} />
      <div className="absolute inset-x-0 bottom-0 sm:inset-auto sm:left-1/2 sm:top-1/2 sm:-translate-x-1/2 sm:-translate-y-1/2 sm:w-full sm:max-w-md">
        <div className="m-0 rounded-t-3xl sm:rounded-3xl bg-white/90 backdrop-blur ring-1 ring-black/10 p-6 shadow-2xl">
          <div className="mb-3 flex items-start gap-3">
            <div className="grid h-10 w-10 place-items-center rounded-xl bg-red-50 text-red-600 ring-1 ring-black/10">
              <WarningIcon className="h-5 w-5" />
            </div>
            <div className="min-w-0">
              <h3 className="text-[16px] font-semibold text-gray-900">{title}</h3>
              <p className="mt-1 text-sm text-gray-600">{message}</p>
            </div>
          </div>

          <div className="mt-5 flex items-center justify-end gap-2">
            <button
              type="button"
              className={`px-4 py-2 text-sm ${QUIET}`}
              onClick={onCancel}
            >
              {t("common.cancel")}
            </button>
            <button
              type="button"
              disabled={confirming}
              className="rounded-xl px-4 py-2 text-sm font-semibold text-white shadow-sm"
              style={{ background: "#DC2626" }}
              onClick={onConfirm}
            >
              {confirming ? t("hoa.announcements.deleting") : t("common.delete")}
            </button>
          </div>
        </div>
      </div>
    </div>
  )
}

/** ---------- Style helpers ---------- */
function getPriorityRail(p: Priority) {
  switch (p) {
    case "urgent":
      return "#DC2626" // red-600
    case "high":
      return "#EF4444" // red-500
    case "medium":
      return "#F59E0B" // amber-500
    case "low":
    default:
      return "#22C55E" // green-500
  }
}

function getPriorityColor(p: Priority) {
  switch (p) {
    case "urgent":
      return "#991B1B" // red-800
    case "high":
      return "#DC2626" // red-600
    case "medium":
      return "#D97706" // amber-600
    case "low":
    default:
      return "#16A34A" // green-600
  }
}

function getPriorityBg(p: Priority) {
  switch (p) {
    case "urgent":
      return "rgba(254,202,202,0.5)" // red-200
    case "high":
      return "rgba(254,215,170,0.35)" // orange-200
    case "medium":
      return "rgba(191,219,254,0.5)" // blue-200
    case "low":
    default:
      return "rgba(187,247,208,0.5)" // green-200
  }
}

function capitalize(s: string) {
  return s.charAt(0).toUpperCase() + s.slice(1)
}

function getPriorityIcon(p: Priority) {
  switch (p) {
    case "urgent":
      return AlertCircleIcon
    case "high":
      return WarningIcon
    case "medium":
      return SpeakerIcon
    case "low":
    default:
      return InfoIcon
  }
}

/** ---------- Icons (inline, no deps) ---------- */
function PlusIcon({ className = "" }: { className?: string }) {
  return (
    <svg className={className} viewBox="0 0 24 24" fill="none" aria-hidden>
      <circle cx="12" cy="12" r="9" stroke="currentColor" strokeWidth="1.8" opacity="0.9" />
      <path d="M12 8v8M8 12h8" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
    </svg>
  )
}
function CalendarIcon({ className = "" }: { className?: string }) {
  return (
    <svg className={className} viewBox="0 0 24 24" fill="none" aria-hidden>
      <rect x="3" y="4.5" width="18" height="16" rx="2" stroke="currentColor" strokeWidth="1.6" />
      <path d="M3 9h18" stroke="currentColor" strokeWidth="1.6" />
      <path d="M8 3v3M16 3v3" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" />
    </svg>
  )
}
function PencilIcon({ className = "" }: { className?: string }) {
  return (
    <svg className={className} viewBox="0 0 24 24" fill="none" aria-hidden>
      <path d="M3 17.25V21h3.75L18.81 8.94l-3.75-3.75L3 17.25z" stroke="currentColor" strokeWidth="1.8" />
      <path d="M15.06 5.19 18.81 8.94" stroke="currentColor" strokeWidth="1.8" />
    </svg>
  )
}
function TrashIcon({ className = "" }: { className?: string }) {
  return (
    <svg className={className} viewBox="0 0 24 24" fill="none" aria-hidden>
      <path d="M4 7h16M9 7V5h6v2M7 7l1 13h8l1-13" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" />
    </svg>
  )
}
function SpeakerIcon({ className = "" }: { className?: string }) {
  return (
    <svg className={className} viewBox="0 0 24 24" fill="none" aria-hidden>
      <path d="M4 10h4l5-4v12l-5-4H4z" stroke="currentColor" strokeWidth="1.8" strokeLinejoin="round" />
      <path d="M17 10a2 2 0 0 1 0 4M19 8a5 5 0 0 1 0 8" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" />
    </svg>
  )
}
function WarningIcon({ className = "" }: { className?: string }) {
  return (
    <svg className={className} viewBox="0 0 24 24" fill="none" aria-hidden>
      <path
        d="M12 8v4m0 4h.01M10.29 3.86 1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0Z"
        stroke="currentColor"
        strokeWidth="1.7"
        strokeLinecap="round"
      />
    </svg>
  )
}
function AlertCircleIcon({ className = "" }: { className?: string }) {
  return (
    <svg className={className} viewBox="0 0 24 24" fill="none" aria-hidden>
      <circle cx="12" cy="12" r="9" stroke="currentColor" strokeWidth="2" />
      <path d="M12 8v4m0 4h.01" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
    </svg>
  )
}
function InfoIcon({ className = "" }: { className?: string }) {
  return (
    <svg className={className} viewBox="0 0 24 24" fill="none" aria-hidden>
      <circle cx="12" cy="12" r="9" stroke="currentColor" strokeWidth="1.8" />
      <path d="M12 8h.01M11 11h2v5h-2z" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" />
    </svg>
  )
}
function PinIcon({ className = "" }: { className?: string }) {
  return (
    <svg className={className} viewBox="0 0 24 24" fill="none" aria-hidden>
      <path
        d="M16 12V4H8v8l-2 2v2h5v6l1 1 1-1v-6h5v-2l-2-2z"
        stroke="currentColor"
        strokeWidth="1.6"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  )
}
